"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import type { Post } from "@/payload-types";
import { SectionHeader } from "fansunited-sports-ui";
import { HorizontalScroll } from "@/components/ui/HorizontalScroll";
import { VideoModal } from "@/components/ui/modals/VideoModal";
import { MediaVariantsProvider } from "@/components/ui/MediaVariantsProvider";
import { VideoCard, type VideoCardSize } from "./VideoCard";
export type VideoDisplayMode = "vertical" | "landscape" | "auto";
export interface VideoCarouselStyleProps {
  gradientFrom?: string;
  gradientTo?: string;
  gradientDirection?: string;
  backgroundImageUrl?: string;
  textColor?: string;
}
export interface VideoCarouselProps {
  videos: Post[];
  displayMode?: VideoDisplayMode;
  cardSize?: VideoCardSize;
  /** Overlay the title on the thumbnail instead of below it. */
  overlayTitle?: boolean;
  title?: string;
  subtitle?: string;
  badgeUrl?: string;
  style?: VideoCarouselStyleProps;
}
const GRADIENT_DIRECTION_MAP: Record<string, string> = {
  "to-bottom": "to bottom",
  "to-right": "to right",
  "to-bottom-right": "to bottom right",
  "to-top": "to top"
};
function getCardDisplayMode(post: Post, displayMode: VideoDisplayMode): "vertical" | "landscape" {
  if (displayMode === "auto") {
    return post.videoKind === "short" ? "vertical" : "landscape";
  }
  return displayMode;
}
function buildSectionStyle(style?: VideoCarouselStyleProps): React.CSSProperties | undefined {
  if (!style) return undefined;
  const css: React.CSSProperties = {};
  if (style.gradientFrom || style.gradientTo) {
    const dir = GRADIENT_DIRECTION_MAP[style.gradientDirection || "to-bottom"] || "to bottom";
    const from = style.gradientFrom || "transparent";
    const to = style.gradientTo || "transparent";
    css.background = `linear-gradient(${dir}, ${from}, ${to})`;
  }
  if (style.backgroundImageUrl) {
    const bgImage = `url(${style.backgroundImageUrl})`;
    css.backgroundImage = css.background ? `${bgImage}, ${css.background}` : bgImage;
    css.backgroundSize = "cover";
    css.backgroundPosition = "center";
    if (!css.background) {
      css.background = undefined;
    }
  }
  if (style.textColor) {
    css.color = style.textColor;
  }
  return Object.keys(css).length > 0 ? css : undefined;
}
export const VideoCarousel: React.FC<VideoCarouselProps> = t0 => {
  const $ = _c(31);
  const {
    videos,
    displayMode: t1,
    cardSize: t2,
    overlayTitle: t3,
    title,
    subtitle,
    badgeUrl,
    style
  } = t0;
  const displayMode = t1 === undefined ? "auto" : t1;
  const cardSize = t2 === undefined ? "md" : t2;
  const overlayTitle = t3 === undefined ? false : t3;
  const [selectedVideo, setSelectedVideo] = useState(null);
  if (!videos || videos.length === 0) {
    return null;
  }
  let t4;
  if ($[0] !== style) {
    t4 = buildSectionStyle(style);
    $[0] = style;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const sectionStyle = t4;
  const hasStyle = !!sectionStyle;
  const t5 = `w-full ${hasStyle ? "rounded-lg p-4" : ""}`;
  let t6;
  if ($[2] !== badgeUrl || $[3] !== style?.textColor || $[4] !== subtitle || $[5] !== title) {
    t6 = title && <SectionHeader entity={{
      name: title,
      imageUrl: badgeUrl,
      subtitle,
      styles: {
        typography: {
          name: `text-lg font-bold tracking-wider ${style?.textColor ? "" : "text-foreground"}`,
          subtitle: `text-sm ${style?.textColor ? "" : "text-muted-foreground"}`
        },
        imageSize: "xl"
      }
    }} styles={{
      container: "mb-3"
    }} />;
    $[2] = badgeUrl;
    $[3] = style?.textColor;
    $[4] = subtitle;
    $[5] = title;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== videos) {
    t7 = videos.map(_temp);
    $[7] = videos;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== cardSize || $[10] !== displayMode || $[11] !== overlayTitle || $[12] !== videos) {
    let t9;
    if ($[14] !== cardSize || $[15] !== displayMode || $[16] !== overlayTitle) {
      t9 = post_0 => <VideoCard key={post_0.id} post={post_0} displayMode={getCardDisplayMode(post_0, displayMode)} cardSize={cardSize} overlayTitle={overlayTitle} onClick={() => setSelectedVideo(post_0)} />;
      $[14] = cardSize;
      $[15] = displayMode;
      $[16] = overlayTitle;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    t8 = videos.map(t9);
    $[9] = cardSize;
    $[10] = displayMode;
    $[11] = overlayTitle;
    $[12] = videos;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <HorizontalScroll gap="gap-3" arrowSize="md">{t8}</HorizontalScroll>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t7 || $[21] !== t9) {
    t10 = <MediaVariantsProvider media={t7}>{t9}</MediaVariantsProvider>;
    $[20] = t7;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== selectedVideo) {
    t11 = selectedVideo && <VideoModal isOpen={!!selectedVideo} onClose={() => setSelectedVideo(null)} video={{
      url: selectedVideo.videoEmbed?.url,
      platform: selectedVideo.videoEmbed?.platform,
      embedCode: selectedVideo.videoEmbed?.embedCode
    }} videoKind={selectedVideo.videoKind} />;
    $[23] = selectedVideo;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== sectionStyle || $[26] !== t10 || $[27] !== t11 || $[28] !== t5 || $[29] !== t6) {
    t12 = <section className={t5} style={sectionStyle}>{t6}{t10}{t11}</section>;
    $[25] = sectionStyle;
    $[26] = t10;
    $[27] = t11;
    $[28] = t5;
    $[29] = t6;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
};
function _temp(post) {
  return post.heroImage;
}